/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: apiKeysApi.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    API calls for API Keys module.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders, API_CONFIG } from '../../../config/api';
import { ApiKey, ListApiKeysResponse, ApiKeyFormData, FetchApiKeysParams } from '../types';

export async function fetchApiKeys(
  params: FetchApiKeysParams,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<ListApiKeysResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
  });

  if (params.search) {
    queryParams.set('search', params.search);
  }
  if (params.sortBy) {
    queryParams.set('sortBy', params.sortBy);
  }
  if (params.sortOrder) {
    queryParams.set('sortOrder', params.sortOrder);
  }

  const response = await fetch(`${buildApiUrl(API_CONFIG.ENDPOINTS.API_KEYS)}?${queryParams}`, {
    method: 'GET',
    headers: getApiHeaders(tenantSlug, userId),
  });

  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || response.statusText);
  }

  return response.json();
}

export async function fetchApiKeyById(
  id: string | number,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<ApiKey> {
  const response = await fetch(buildApiUrl(`${API_CONFIG.ENDPOINTS.API_KEYS}/${id}`), {
    method: 'GET',
    headers: getApiHeaders(tenantSlug, userId),
  });

  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || response.statusText);
  }

  const result = await response.json();
  return result.data;
}

export async function createApiKey(
  data: ApiKeyFormData,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<ApiKey> {
  const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.API_KEYS), {
    method: 'POST',
    headers: getApiHeaders(tenantSlug, userId),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to create API key');
  }

  const result = await response.json();
  return result.data;
}

export async function updateApiKey(
  id: string | number,
  data: Partial<ApiKeyFormData>,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<ApiKey> {
  const response = await fetch(buildApiUrl(`${API_CONFIG.ENDPOINTS.API_KEYS}/${id}`), {
    method: 'PUT',
    headers: getApiHeaders(tenantSlug, userId),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to update API key');
  }

  const result = await response.json();
  return result.data;
}

export async function deleteApiKey(
  id: string | number,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<void> {
  const response = await fetch(buildApiUrl(`${API_CONFIG.ENDPOINTS.API_KEYS}/${id}`), {
    method: 'DELETE',
    headers: getApiHeaders(tenantSlug, userId),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to delete API key');
  }
}

export async function fetchApiKeysQuickList(
  search: string,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<ApiKey[]> {
  const queryParams = new URLSearchParams({
    ...(search && { search }),
  });

  const response = await fetch(
    `${buildApiUrl(API_CONFIG.ENDPOINTS.API_KEYS)}/quicklist?${queryParams}`,
    {
      method: 'GET',
      headers: getApiHeaders(tenantSlug, userId),
    },
  );

  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || response.statusText);
  }

  const result = await response.json();
  return result.data;
}

/**
 * Fetch domains by tenant ID for domain scope selector
 */
export interface DomainOption {
  id: number;
  name: string;
  description?: string;
}

export async function fetchDomainsByTenant(
  tenantId: number,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<DomainOption[]> {
  const queryParams = new URLSearchParams({
    page: '1',
    limit: '100',
    sortBy: 'domainName',
    sortOrder: 'asc'
  });

  const response = await fetch(
    `${buildApiUrl('/api/telephony/domains')}?${queryParams}`,
    {
      method: 'GET',
      headers: getApiHeaders(tenantSlug, userId),
    },
  );

  if (!response.ok) {
    return [];
  }

  const result = await response.json();

  // Filter domains belonging to the selected tenant
  const domains = (result.data || [])
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .filter((d: any) => d.tenant_id === tenantId)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((d: any) => ({
      id: d.id,
      name: d.domainName || d.name,
      description: d.domainDescription || d.description
    }));

  return domains;
}

